import '@patsnap-ui/icon/assets/solid/AddMedium.svg'
import { checkEmailValid } from '@patsnap/synapse_common_utils'
import { E_Feedback_Type } from '@pharmsnap/shared/src/types'
import { computed, defineComponent, getCurrentInstance, ref, watch } from '@vue/composition-api'
import { ElUploadInternalFileDetail } from 'element-ui/types/upload'
import { Icon as PtIcon } from 'patsnap-biz'
import { GDialog } from 'pharmsnapMF_shared/components'
import { useAuthStore } from 'pharmsnapMF_shared/composition'
import { sharedCtx } from 'pharmsnapMF_shared/context'
import { ElForm, ElFormItem, ElInput, ElRadio, ElRadioGroup, ElUpload } from 'pharmsnapMF_shared/element-ui'
import { showSingleToast, transformData } from 'pharmsnapMF_shared/utils'
import { ErrorType } from '../Feedback'
import $styles from './FeedbackDialog.module.scss'
import cn from './locales/cn.json'
import en from './locales/en.json'
import jp from './locales/jp.json'

export default defineComponent({
  name: 'FeedbackDialog',
  i18n: {
    messages: {
      cn,
      en,
      jp,
    },
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  setup(_props, ctx) {
    const ins = getCurrentInstance()
    const images = ref<ElUploadInternalFileDetail[]>([])
    const uploadRef = ref()

    // 当前选中的错误类型，默认为数据问题
    const currentErrorType = ref<ErrorType>('data')

    const formData = computed(() => ({
      description: description.value,
      email: email.value,
      images: images.value,
    }))
    const {
      getters: { email: defaultEmail },
    } = useAuthStore()
    const form = ref()
    const validateRules = {
      description: [{ required: true, message: ins?.proxy.$t('descriptionNoContent'), trigger: 'blur' }],
      email: [
        { required: true, message: ins?.proxy.$t('noEmail'), trigger: 'blur' },
        { type: 'email', message: ins?.proxy.$t('emailInvalid'), trigger: ['blur', 'change'] },
      ],
    }

    const description = ref('')
    const email = ref(defaultEmail.value)
    const isSendingFeedback = ref(false)
    const resetData = () => {
      description.value = ''
      email.value = defaultEmail.value
      images.value = []
      currentErrorType.value = 'data'
    }

    const handleCloseDialog = () => {
      ctx.emit('close')
      resetData()
    }
    const handleBeforeUpload = (file: File) => {
      const isIMAGE = file.type === 'image/jpeg' || 'image/png'
      const isLt4M = file.size / 1024 / 1024 < 4

      if (!isIMAGE) {
        showSingleToast({ message: ins?.proxy.$t('feedback.imgRequired') as string, type: 'error' })
      }
      if (!isLt4M) {
        showSingleToast({ message: ins?.proxy.$t('feedback.exceedSize') as string, type: 'error' })
      }
      return isIMAGE && isLt4M
    }
    const feedTypeMap: Record<ErrorType, E_Feedback_Type> = {
      data: E_Feedback_Type.DATA_FEED,
      function: E_Feedback_Type.FUNCTION_FEED,
    }

    // 提交数据：移除feed_section，新增feed_page为当前页面URL
    const submitData = computed(() => ({
      feed_type: feedTypeMap[currentErrorType.value],
      feed_page: window.location.href,
      feed_detail: description.value,
      email: email.value,
      images: images.value.map((item) => item.raw),
    }))
    const handleSubmit = async () => {
      let hasError = false
      isSendingFeedback.value = true
      submitData.value.images.forEach((file) => {
        if (!handleBeforeUpload(file)) {
          hasError = true
          return
        }
      })
      if (hasError) {
        isSendingFeedback.value = false
        return
      }
      ctx.emit('close')
      const res = await sharedCtx.service.home.sendFeedback(transformData(submitData.value))
      resetData()
      if (res.success) {
        ctx.emit('submitSuccess')
      } else {
        showSingleToast({ message: ins?.proxy.$t('feedback.submitFail') as string, type: 'error' })
      }

      isSendingFeedback.value = false
    }
    const handleImageChange = (_file: ElUploadInternalFileDetail, fileList: ElUploadInternalFileDetail[]) => {
      images.value = fileList
    }

    watch(images, (newVal) => {
      const inputElement = uploadRef.value.$el.querySelector('input[type="file"]')
      if (newVal.length >= 2) {
        inputElement.disabled = true
      } else {
        inputElement.disabled = false
      }
    })

    return {
      handleSubmit,
      email,
      description,
      images,
      validateRules,
      formData,
      form,
      isSendingFeedback,
      handleCloseDialog,
      handleBeforeUpload,
      uploadRef,
      handleImageChange,
      currentErrorType,
    }
  },
  methods: {},
  render() {
    return (
      <GDialog
        append-to-body={true}
        destroy-on-close={true}
        close-on-click-modal={false}
        title={this.$t('feedback.reportProblem') as string}
        visible={this.visible}
        width="500px"
        onClose={this.handleCloseDialog}
        onCancel={this.handleCloseDialog}
        confirmDisabled={!(this.formData.description && this.formData.email && checkEmailValid(this.formData.email)) || this.isSendingFeedback}
        confirmText={this.$t('submit') as string}
        onConfirm={() => this.form.validate((valid: boolean) => valid && this.handleSubmit())}
      >
        <ElForm
          ref="form"
          rules={this.validateRules}
          {...{
            props: {
              model: this.formData,
            },
          }}
          class={$styles.form}
        >
          {/* 问题类型切换 */}
          <div class="mb-4">
            <div class="text-sm font-semibold mb-2 text-text-t1">{this.$t('feedback.problemType')}</div>
            <ElRadioGroup vModel={this.currentErrorType} class="w-full">
              <ElRadio label="data" class="mr-6">
                {this.$t('feedback.dataError')}
              </ElRadio>
              <ElRadio label="function">{this.$t('feedback.functionError')}</ElRadio>
            </ElRadioGroup>
          </div>

          {/* 问题描述 */}
          <ElFormItem prop="description">
            <ElInput
              class="mt-2"
              type="textarea"
              rows="10"
              placeholder={this.$t('textareaTip')}
              maxlength={500}
              showWordLimit={true}
              vModel={this.formData.description}
              onInput={(value: string) => (this.description = value)}
            ></ElInput>
          </ElFormItem>

          {/* 图片上传 */}
          <ElUpload
            ref="uploadRef"
            autoUpload={false}
            {...{
              props: {
                onChange: this.handleImageChange,
                onRemove: this.handleImageChange,
              },
            }}
            multiple={true}
            action=""
            limit={2}
            accept="image/png, image/jpeg"
          >
            <button slot="trigger" class="pt-ui-btn" with-icon data-type="submit" type="button" disabled={this.images.length >= 2}>
              <PtIcon icon="SolidAddMedium" />
              <span>{this.$t('feedback.addImage')}</span>
              &nbsp;({this.images.length}/2)
            </button>
          </ElUpload>

          {/* 邮箱输入 */}
          <div class="text-sm font-semibold mb-1 mt-5 text-text-t1">{this.$t('email')}</div>
          <ElFormItem prop="email" style="margin-bottom: 0">
            <ElInput
              class="mt-2"
              placeholder={this.$t('emailTip')}
              type="email"
              vModel={this.formData.email}
              onInput={(value: string) => (this.email = value)}
            ></ElInput>
          </ElFormItem>

          {/* 提示信息 */}
          <div class="flex items-center mt-4">
            <PtIcon icon="SolidInfo" class="w-6 h-6 text-text-t4 text-base " />
            <div class="text-text-t1 text-sm ml-1">{this.$t('infoTip')}</div>
          </div>
        </ElForm>
      </GDialog>
    )
  },
})
